import { useEffect, useRef, useState } from "react";
import { Loader2, Check } from "lucide-react";
import { OcrBaseAlertIcon } from "@/components/OcrBaseAlertIcon";
import { useDocumentGastos, type DocumentGasto } from "@/hooks/useDocumentGastos";
import { useUpdateGasto, type BaseProrrateo } from "@/hooks/useGastosEmbarque";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const fmt = (n: number | null | undefined, fractionDigits = 2) =>
  n == null
    ? "—"
    : Number(n).toLocaleString(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });

const toNum = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Suma agregada por moneda — sin convertir, para no inducir errores TRM. */
function aggregateByCurrency(rows: EditableGasto[]) {
  const map = new Map<string, { base: number; iva: number; total: number }>();
  for (const g of rows) {
    const cur = (g.moneda ?? "").toUpperCase() || "—";
    const acc = map.get(cur) ?? { base: 0, iva: 0, total: 0 };
    acc.base += toNum(g.monto_base);
    acc.iva += toNum(g.impuestos_locales);
    acc.total += toNum(g.monto);
    map.set(cur, acc);
  }
  return Array.from(map.entries()).map(([moneda, v]) => ({ moneda, ...v }));
}

/* ------------------------------------------------------------------ */
/* Confidence pill — semantic tokens                                   */
/* ------------------------------------------------------------------ */

function ConfidencePill({ value }: { value: number | null }) {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  const tone =
    value >= 0.9
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30"
      : value >= 0.7
        ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30"
        : "bg-destructive/15 text-destructive border-destructive/30";
  return (
    <Badge
      variant="outline"
      className={cn("font-mono text-[10px] px-1.5 py-0 font-medium tabular-nums", tone)}
    >
      {pct}%
    </Badge>
  );
}

/* ------------------------------------------------------------------ */
/* Editable type — local working state with dirty flag                 */
/* ------------------------------------------------------------------ */

type EditableGasto = DocumentGasto & { _edited?: boolean };

const CURRENCIES = ["COP", "USD", "EUR", "MXN", "CNY"] as const;
const PRORRATEO: BaseProrrateo[] = ["Peso", "Valor"];

/* ------------------------------------------------------------------ */
/* Public — Cards editables, gemelas a las de Ítems                    */
/* ------------------------------------------------------------------ */

interface Props {
  documentoId: string;
  /** Kept for backwards-compat with EditDocumentDrawer. */
  variant?: "table" | "cards";
}

export function ExtractedExpensesSection({ documentoId }: Props) {
  const { data: gastos = [], isLoading } = useDocumentGastos(documentoId);
  const updateGasto = useUpdateGasto();
  const { toast } = useToast();

  const [rows, setRows] = useState<EditableGasto[]>([]);
  const initialRef = useRef<DocumentGasto[]>([]);

  // Hidratar el estado local cuando llegan los datos
  useEffect(() => {
    initialRef.current = gastos.map((g) => ({ ...g }));
    setRows(gastos.map((g) => ({ ...g })));
  }, [gastos]);

  const handleChange = <K extends keyof DocumentGasto>(
    idx: number,
    key: K,
    value: DocumentGasto[K],
  ) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value, _edited: true };
      return next;
    });
  };

  const handleSave = async (idx: number) => {
    const row = rows[idx];
    if (!row?._edited) return;
    if (!row.embarque_id) {
      toast({
        title: "Gasto sin embarque",
        description: "Este gasto aún no está asignado a un embarque y no puede editarse aquí.",
        variant: "destructive",
      });
      return;
    }
    try {
      await updateGasto.mutateAsync({
        id: row.id,
        embarque_id: row.embarque_id,
        concepto: row.concepto,
        monto: toNum(row.monto),
        moneda: row.moneda,
        base_prorrateo: row.base_prorrateo as BaseProrrateo,
        trm_pago: row.trm_pago,
        aplica_costeo_inventario: row.aplica_costeo_inventario,
        notas: row.notas,
      });
      setRows((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], _edited: false };
        return next;
      });
      toast({ title: "Gasto actualizado", description: row.concepto });
    } catch (e: any) {
      toast({
        title: "Error al guardar",
        description: e?.message ?? "Inténtalo de nuevo",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        <span className="text-xs">Cargando gastos extraídos…</span>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="text-xs text-muted-foreground text-center py-6">
        No se extrajeron gastos para este documento
      </p>
    );
  }

  const totalsByCurrency = aggregateByCurrency(rows);

  return (
    <div className="space-y-3">
      {rows.map((g, idx) => {
        const base = toNum(g.monto_base);
        const iva = toNum(g.impuestos_locales);
        const total = toNum(g.monto);
        const showBreakdown = base > 0 || iva > 0;

        return (
          <Card
            key={g.id}
            className={cn(
              "border-border transition-colors",
              g._edited && "border-primary/40 bg-primary/5",
            )}
          >
            <CardContent className="p-3 space-y-2">
              {/* Header: index + costeo flag + estado */}
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono text-muted-foreground">
                  #{idx + 1}
                </span>
                <div className="flex items-center gap-2">
                  {g._edited && (
                    <span className="text-[10px] text-primary font-medium">Modificado</span>
                  )}
                  {g.aplica_costeo_inventario ? (
                    <Badge className="text-[10px] px-1.5 py-0 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 font-medium gap-0.5">
                      <Check className="h-2.5 w-2.5" />
                      Costeo
                    </Badge>
                  ) : (
                    <span className="text-[10px] text-muted-foreground">OPEX</span>
                  )}
                </div>
              </div>

              {/* Concepto (full width) */}
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">Concepto</Label>
                <Input
                  className="h-7 text-xs"
                  value={g.concepto ?? ""}
                  title={g.concepto ?? ""}
                  onChange={(e) => handleChange(idx, "concepto", e.target.value)}
                />
              </div>

              {/* Moneda + Prorrateo */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">Moneda</Label>
                  <Select
                    value={g.moneda ?? ""}
                    onValueChange={(v) => handleChange(idx, "moneda", v)}
                  >
                    <SelectTrigger className="h-7 text-xs bg-card">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c} value={c} className="text-xs">
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">Base Prorrateo</Label>
                  <Select
                    value={g.base_prorrateo ?? "Valor"}
                    onValueChange={(v) => handleChange(idx, "base_prorrateo", v)}
                  >
                    <SelectTrigger className="h-7 text-xs bg-card">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRORRATEO.map((p) => (
                        <SelectItem key={p} value={p} className="text-xs">
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Base + IVA breakdown — solo si OCR los desglosó (read-only, vienen calculados) */}
              {showBreakdown && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                      Base
                      <OcrBaseAlertIcon active={g.alerta_ocr_base} />
                    </Label>
                    <Input
                      className="h-7 text-xs bg-muted/50 font-mono tabular-nums"
                      readOnly
                      value={fmt(base)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground">+ IVA</Label>
                    <Input
                      className="h-7 text-xs bg-muted/50 font-mono tabular-nums"
                      readOnly
                      value={fmt(iva)}
                    />
                  </div>
                </div>
              )}

              {/* Total + TRM */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">= Total</Label>
                  <Input
                    className="h-7 text-xs font-mono font-semibold tabular-nums"
                    type="number"
                    step="0.01"
                    value={g.monto ?? ""}
                    onChange={(e) => handleChange(idx, "monto", toNum(e.target.value))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">TRM Pago</Label>
                  <Input
                    className="h-7 text-xs font-mono tabular-nums"
                    type="number"
                    step="0.01"
                    value={g.trm_pago ?? ""}
                    placeholder="—"
                    onChange={(e) =>
                      handleChange(
                        idx,
                        "trm_pago",
                        e.target.value === "" ? null : toNum(e.target.value),
                      )
                    }
                  />
                </div>
              </div>

              {/* Costeo toggle */}
              <div className="flex items-center justify-between rounded-md border border-border/60 bg-muted/30 px-2 py-1.5">
                <Label className="text-[10px] text-muted-foreground">
                  Aplica al costeo de inventario
                </Label>
                <Switch
                  checked={!!g.aplica_costeo_inventario}
                  onCheckedChange={(v) => handleChange(idx, "aplica_costeo_inventario", v)}
                />
              </div>

              {/* Notas */}
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">Notas</Label>
                <Input
                  className="h-7 text-xs"
                  value={g.notas ?? ""}
                  placeholder="—"
                  onChange={(e) => handleChange(idx, "notas", e.target.value)}
                />
              </div>

              {/* Footer meta + acción guardar */}
              <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-border/40">
                <ConfidencePill value={g.confianza_ocr} />
                {g._edited && (
                  <Button
                    size="sm"
                    className="h-6 text-[10px] px-2 ml-auto"
                    onClick={() => handleSave(idx)}
                    disabled={updateGasto.isPending}
                  >
                    {updateGasto.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      "Guardar"
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Subtotales por moneda — agregado fiscal sin convertir */}
      {totalsByCurrency.length > 0 && (
        <div className="border-t border-border pt-2 space-y-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-1">
            Subtotales por moneda
          </p>
          {totalsByCurrency.map((t) => (
            <div
              key={t.moneda}
              className="flex items-center justify-between px-1 text-xs tabular-nums"
            >
              <Badge
                variant="outline"
                className="font-mono text-[10px] px-1.5 py-0 font-semibold"
              >
                {t.moneda}
              </Badge>
              <span className="font-bold text-foreground">{fmt(t.total)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
